import { createReducer, on, props } from '@ngrx/store';

import * as clientDataActions from './clientData.actions';
import { BatchChange } from './clientData.actions';
import { patchJobsAction } from './store/scheduler/actions';
import {
  IReasonCode,
  ILocation,
  ITask,
  ISystemPreferences,
  ISystemInfo,
  IBundlingRulesDocument,
  IMachine,
  IMetricDefinition,
  IMachineStateDto,
  IRollformingStatistics,
  IStatisticsHistory,
  IMachineMetricSettings,
  IScheduleSummary,
  IScheduleEstimate,
  ICoilDto,
  IMaterialDto,
  IJobSummaryDto,
  IJobDetailDto,
  IScheduledDowntimeDto,
  IAvailableJob,
  ISchedule,
  IAlert,
  IHealth,
  IPendingActionsToAgent,
  ISyncState,
  IAndonSequenceConfig,
  IAndonView,
  IConsumptionHistory,
  ITaskFacet,
  IUserTaskFilters,
  IAvailableJobColumn,
  IBundleResult,
  IOrderImportEvent,
  ICoilImportEvent,
  IOrderImportConfig,
  ICoilImportConfig,
  IExportConfig,
  IExportEvent,
  IMaterialImportConfig,
  IMaterialImportEvent,
  IScheduleSyncConfig,
  IScheduleSyncEvent,
  IDevice,
  IDeviceState,
  IDeviceMetrics,
  IDeviceShiftStatistics,
  IWebhookConfig,
  IWebhookEvent,
  IPrintTemplate,
  IMachinePrintConfig,
  IInstalledPrinters,
  ILicense,
  IWallboardDevice,
  IUser,
  Fx,
  ILanguage,
  IProductionSummaryReportRecord,
  IPathfinderMachine,
  ToolingHeader,
  IProductionEvent,
  ToolingDef,
  CommTraceBatch,
  ExpressCommState,
  ExpressCtrlState,
  IRecentBundleResult,
  IPunchPattern,
  MachinePattern,
  WarehouseViewModel,
  IExternalConnection,
  IMaterialColor,
  IMaterialType,
  IUpdateInfo,
  ICmmsExportConfig,
  ITruckInstanceState,
} from 'src/app/core/dto';
import { BundleRules } from './store/order/selectors';

let _putOne = <T extends { id: string | number }>(
  state: Array<T>,
  payload: T,
  collection: string
): Array<T> => {
  if (typeof payload !== 'object' || payload === null) {
    //we should fix the server but not crash if we get bad data.
    console.error('`payload` is not an object or is null', payload);
    return state;
  }
  if (!('id' in payload)) {
    console.error(payload);
    throw "object in collection '" + collection + "' does not have 'id' property";
  }
  let tobj = state.find((t) => t.id === payload.id);
  if (tobj) {
    return state.map((t) => (t.id === payload.id ? payload : t));
  } else {
    return [...state, payload];
  }
};
let _delOne = <T extends { id: string | number }>(
  state: Array<T>,
  id: string,
  collection: string
) => {
  // Sometimes ids have the document type and sometimes they don't

  // todo: this doesn't belong here. Find a better place.
  // This collection is sourced from a raven artificial document. It seems that those get deleted and recreated (at least this one).
  // When that happens, the screen flashes.
  if (collection === 'MachineStatistics') {
    return state;
  }
  let idOnly = id.indexOf('/') > 0 ? id.substring(id.indexOf('/') + 1) : id;
  const kept = state.filter((t) => t.id !== id && t.id !== idOnly);
  // Same reference when nothing was removed. The server sends AvailableJob deletes to every
  // subscriber of the collection, whatever machine they filter on, so most deletes a tab gets are
  // for rows it doesn't have; a fresh array for each one rebuilt the whole scheduler grid.
  return kept.length === state.length ? state : kept;
};

/**
 * Applies a burst of puts and deletes in arrival order, with the same per-item meaning as
 * `_putOne` (replace in place, else append) and `_delOne` (match the id with or without its
 * collection prefix), in one pass instead of one array copy per item. Returns `state` itself when
 * nothing changed.
 */
export const _applyBatch = <T extends { id: string | number }>(
  state: Array<T>,
  changes: BatchChange[],
  collection: string
): Array<T> => {
  const byId = new Map<string | number, T>();
  for (const t of state) {
    byId.set(t.id, t);
  }
  let changed = false;
  for (const change of changes) {
    if (change.type === 'put') {
      const payload = _normalizePayload(collection, change.payload);
      if (typeof payload !== 'object' || payload === null || !('id' in payload)) {
        // _putOne throws on a missing id; one bad row must not take the rest of the batch with it.
        console.error(`Ignoring a PUT without an 'id' in collection '${collection}'`, payload);
        continue;
      }
      // Map.set keeps an existing key's position, which is _putOne's replace-in-place.
      byId.set(payload.id, payload);
      changed = true;
    } else {
      if (collection === 'MachineStatistics') {
        continue; // see _delOne
      }
      const id = change.id;
      const idOnly = id.indexOf('/') > 0 ? id.substring(id.indexOf('/') + 1) : id;
      if (byId.delete(id) || byId.delete(idOnly)) {
        changed = true;
      }
    }
  }
  return changed ? Array.from(byId.values()) : state;
};

const singletons = [
  'SystemPreferences',
  'UpdateInfo',
  'SystemAgent',
  'BundlingRulesDocument',
  'MetricDefinition',
  'Alerts',
  'AndonViews',
  'TaskFilters',
  'InstalledPrinters',
  'License',
  'ExpressCommState',
  'ExpressCommTrace',
  'WarehouseViewModel',
  'MaterialColors',
  'MaterialTypes',
];

const appends = ['JobSummary', 'JobDetail', 'AvailableJob'];

export interface ICollections {
  ReasonCode: IReasonCode[];
  Location: ILocation[];
  MaterialTask: ITask[];
  SystemPreferences: ISystemPreferences;
  UpdateInfo: IUpdateInfo;
  SystemAgent?: any;
  BundlingRulesDocument: IBundlingRulesDocument;
  Machine: IMachine[];
  MetricDefinition: IMetricDefinition[];
  MachineState: IMachineStateDto[];
  MachineStatistics: IRollformingStatistics[];
  MachineStatisticsHistory: IStatisticsHistory[];
  MachineMetricSettings: IMachineMetricSettings[];
  MachineScheduleSummary: IScheduleSummary[];
  MachineSchedule: IScheduleEstimate[];
  Coil: ICoilDto[];
  CoilTypes: IMaterialDto[];
  JobSummary: IJobSummaryDto[];
  JobDetail: IJobDetailDto[];
  ScheduledDowntimeDefinition: IScheduledDowntimeDto[];
  AvailableJob: IAvailableJob[];
  Schedule: ISchedule[];
  Alerts: IAlert[];
  Health: IHealth[];
  PendingActionsToAgent: IPendingActionsToAgent[];
  SyncState: ISyncState[];
  AndonSequenceConfig: IAndonSequenceConfig[];
  AndonViews: IAndonView[];
  ConsumptionHistory: IConsumptionHistory[];
  TaskFilters: ITaskFacet[];
  UsersTaskFilters: IUserTaskFilters[];
  BundleResult: IBundleResult[];
  OrderImportEvents: IOrderImportEvent[];
  CoilImportEvents: ICoilImportEvent[];
  OrderImportConfigs: IOrderImportConfig[];
  CoilImportConfigs: ICoilImportConfig[];
  ExportConfigs: IExportConfig[];
  ChannelItemStates: IExportEvent[];
  MaterialImportConfigs: IMaterialImportConfig[];
  MaterialImportEvents: IMaterialImportEvent[];
  ScheduleSyncConfigs: IScheduleSyncConfig[];
  ScheduleSyncEvents: IScheduleSyncEvent[];
  Devices: IDevice[];
  DeviceState: IDeviceState[];
  DeviceMetrics: IDeviceMetrics[];
  DeviceStatisticsByDay: IDeviceShiftStatistics[];
  CoilValidationConfigs: IExportConfig[];
  CoilValidationEvents: IExportEvent[];
  WebhookConfigs: IWebhookConfig[];
  ExternalConnections: IExternalConnection[];
  WebhookEvents: IWebhookEvent[];
  RecentBundles: IRecentBundleResult[];
  PrintTemplates: IPrintTemplate[];
  MachinePrintConfigs: IMachinePrintConfig[];
  InstalledPrinters: IInstalledPrinters;
  UnlicensedMachine: IMachine[];
  License: ILicense;
  WallboardDevices: IWallboardDevice[];
  Users: IUser[];
  BundleRules: BundleRules[];
  Folders: IPathfinderMachine[];
  ToolingItems: ToolingHeader[];
  ToolingDefs: ToolingDef[];
  ExpressCommState: ExpressCommState;
  ExpressCommTrace: CommTraceBatch;
  ExpressCtrlState: ExpressCtrlState[];
  ProductionEvents: IProductionEvent[];
  PunchPatterns: IPunchPattern[];
  MachinePattern: MachinePattern[];
  WarehouseViewModel: WarehouseViewModel;
  MaterialColors: IMaterialColor[];
  MaterialTypes: IMaterialType[];
  CmmsExportConfigs: ICmmsExportConfig[];
  TruckInstanceState: ITruckInstanceState[];
}

const initCollections: ICollections = {
  ReasonCode: [],
  Location: [],
  MaterialTask: [],
  SystemPreferences: {
    systemLanguage: 'en',
    allowPrereleaseVersions: false,
    inchesUnit: 'in',
    intranetUrl: null,
    redirectFromLocalhost: false,
    allowGuestUser: false,
    plantName: '',
    showMaterialShortageAlerts: false,
    showTaskErrorAlerts: false,
    hideAgentOfflineAlert: false,
    hideCommOfflineAlert: false,
    hideMachineOfflineAlert: false,
    enableMaterialTasks: false,
    serverIpAddress: '',
  },
  UpdateInfo: {} as IUpdateInfo,
  BundlingRulesDocument: {
    systemLevel: {
      maxWeightLbs: 0,
      maxPieceCount: 0,
      minPctOfMaxLength: 0,
      itemSort: null,
    },
    customerRules: {},
    toolingDefRules: {},
    materialToolingRules: [],
    customerToolingRules: [],
  },
  Machine: [],
  MetricDefinition: [],
  MachineState: [],
  MachineStatistics: [],
  MachineStatisticsHistory: [],
  MachineMetricSettings: [],
  MachineScheduleSummary: [],
  MachineSchedule: [],
  Coil: [],
  CoilTypes: [],
  JobSummary: [],
  JobDetail: [],
  ScheduledDowntimeDefinition: [],
  AvailableJob: [],
  Schedule: [],
  Alerts: [],
  Health: [],
  PendingActionsToAgent: [],
  SyncState: [],
  AndonSequenceConfig: [],
  AndonViews: [],
  ConsumptionHistory: [],
  TaskFilters: [],
  UsersTaskFilters: [],
  BundleResult: [],
  OrderImportEvents: [],
  CoilImportEvents: [],
  OrderImportConfigs: [],
  CoilImportConfigs: [],
  ExportConfigs: [],
  ChannelItemStates: [],
  MaterialImportConfigs: [],
  MaterialImportEvents: [],
  ScheduleSyncConfigs: [],
  ScheduleSyncEvents: [],
  Devices: [],
  DeviceState: [],
  DeviceMetrics: [],
  DeviceStatisticsByDay: [],
  CoilValidationConfigs: [],
  CoilValidationEvents: [],
  WebhookConfigs: [],
  ExternalConnections: [],
  WebhookEvents: [],
  RecentBundles: [],
  PrintTemplates: [],
  MachinePrintConfigs: [],
  InstalledPrinters: {
    printers: [],
  },
  UnlicensedMachine: [],
  License: {
    serverId: '',
    lastUpdate: '',
    updateCount: 0,
    modules: [],
    machines: [],
    deviceLicensePools: [],
    deviceLicenseAssignments: [],
  },
  WallboardDevices: [],
  Users: [],
  BundleRules: [],
  Folders: [],
  ToolingItems: [],
  ToolingDefs: [],
  ExpressCommState: {
    id: 'ExpressCommState',
  },
  ExpressCommTrace: {
    id: 'ExpressCommTrace',
    seed: false,
    droppedCount: 0,
    events: [],
  },
  ExpressCtrlState: [],
  ProductionEvents: [],
  PunchPatterns: [],
  MachinePattern: [],
  WarehouseViewModel: {
    id: 'WarehouseViewModel',
    activeTasks: [],
    completeTasks: [],
    readyTasks: [],
  },
  MaterialColors: [],
  MaterialTypes: [],
  CmmsExportConfigs: [],
  TruckInstanceState: [],
};

// Map documentID to id for MachinePattern
const _normalizePayload = (collection: string, payload: any): any => {
  if (collection === 'MachinePattern' && payload && payload.documentID && !payload.id) {
    return { ...payload, id: payload.documentID };
  }
  return payload;
};

export const collectionReducer = createReducer(
  initCollections,
  on(
    clientDataActions.putAction,
    (state: ICollections, { collection, payload }: { collection: string; payload: any }) => {
      if (!state.hasOwnProperty(collection)) {
        console.log('Received PUT for collection not managed on ICollection', collection, payload);
        return state;
      }
      const normalizedPayload = _normalizePayload(collection, payload);
      if (singletons.includes(collection)) {
        return {
          ...state,
          [collection]: normalizedPayload,
        };
      }
      return {
        ...state,
        [collection]: _putOne(state[collection], normalizedPayload, collection),
      };
    }
  ),
  on(
    clientDataActions.delAction,
    (state: ICollections, { collection, id }: { collection: string; id: string }) => {
      if (!state.hasOwnProperty(collection)) {
        console.log('Received DEL for collection not managed on ICollection', collection, id);
        return state;
      }
      if (singletons.includes(collection)) {
        return { ...state };
      }
      return {
        ...state,
        [collection]: _delOne(state[collection], id, collection),
      };
    }
  ),
  on(
    clientDataActions.initializeAction,
    (state: ICollections, { collection, payload }: { collection: string; payload: any }) => {
      if (!singletons.includes(collection)) {
        return {
          ...state,
          [collection]: payload
            .filter((obj) => obj !== null && obj !== undefined)
            .map((obj: any) => _normalizePayload(collection, obj))
            .reduce((state, obj) => {
              return _putOne(state, obj, collection);
            }, state[collection]),
        };
      }
      return {
        ...state,
        [collection]: _normalizePayload(collection, payload),
      };
    }
  ),
  on(
    clientDataActions.applyBatchAction,
    (state: ICollections, { collection, changes }: { collection: string; changes: BatchChange[] }) => {
      if (!state.hasOwnProperty(collection) || singletons.includes(collection)) {
        // The hub only batches array collections; anything else is a wiring mistake.
        console.log('Received a batch for a collection that is not a managed array', collection);
        return state;
      }
      const next = _applyBatch(state[collection], changes, collection);
      return next === state[collection] ? state : { ...state, [collection]: next };
    }
  ),
  on(
    clientDataActions.chopAction,
    (
      state: ICollections,
      { collection, payload }: { collection: string; payload: Fx.Subscription[] }
    ) => {
      if (singletons.includes(collection)) {
        return state;
      }
      const chopFilter = payload
        .map((s) => Fx.toFilterExpr(s.filterDef))
        .reduce(
          (acc, filt) => (t) => acc(t) || filt(t),
          (_) => false
        );
      const kept = state[collection].filter(chopFilter);
      // Same reference when nothing was chopped. A fresh array here wakes every selector on the
      // collection, and a subscriber that reacts by re-subscribing (and so deleting again) turns
      // that into a synchronous dispatch loop - seen on the wallboard settings page once the
      // andon subscriptions were properly torn down.
      if (kept.length === state[collection].length) {
        return state;
      }
      return {
        ...state,
        [collection]: kept,
      };
    }
  ),
  on(patchJobsAction, (state: ICollections, { ordIds, collection, patch }) => {
    if (!appends.includes(collection)) {
      return state;
    }
    const newJobs = state[collection].map((x) => {
      if (Array.isArray(ordIds)) {
        if (ordIds.includes(x.ordId)) {
          return { ...x, ...patch };
        }
      } else if (ordIds === x.ordId) {
        return { ...x, ...patch };
      }

      return x;
    });
    return {
      ...state,
      [collection]: newJobs,
    };
  })
);
